import styled from 'styled-components';

import { Text } from '../../../../cdl/Text/Text';
import { translate } from '../../../../common/helpers/translate.helper';
import { OfferStatusTag } from '../../../../common/OfferStatusTag/OfferStatusTag';
import { Section } from '../../../../common/Section/Section';
import { Box } from '../../../../common/ui/Box';
import { theme } from '../../../../common/ui/theme';
import { OfferHistoryTransition } from '../../history/buildOfferHistoryTransitions';
import { OfferHistoryProductDiffTable } from '../../history/components/OfferHistoryProductDiffTable';
import { getCommercialFieldLabel, getCoreFieldLabel } from '../../history/helpers/offerHistoryLabels';
import { useOfferHistoryFormatting } from '../../history/hooks/useOfferHistoryFormatting';

const CompareGrid = styled(Box)`
    display: grid;
    grid-template-columns: minmax(120px, max-content) minmax(0, 1fr) minmax(0, 1fr);
    gap: ${theme.space[12]} ${theme.space[16]};
    align-items: start;
`;

interface OfferHistorySummaryViewProps {
    transition: OfferHistoryTransition;
}

export const OfferHistorySummaryView = ({ transition }: OfferHistorySummaryViewProps) => {
    const formatOfferValue = useOfferHistoryFormatting();
    const hasDetailChanges =
        transition.changedSections.includes('state') ||
        transition.coreDataFields.length > 0 ||
        transition.commercialFields.length > 0;

    return (
        <Box display="flex" flexDirection="column" gap={24}>
            {hasDetailChanges ? (
                <Section title={translate('offer.detail.details')} fullHeight={false}>
                    <CompareGrid>
                        <Text />
                        <Text variant="fieldLabel">{translate('offer.history.compare.before')}</Text>
                        <Text variant="fieldLabel">{translate('offer.history.compare.after')}</Text>

                        {transition.changedSections.includes('state') ? (
                            <Box display="contents">
                                <Text variant="fieldLabel">{translate('offer.history.field.state')}</Text>
                                <Box width="fit-content">
                                    <OfferStatusTag state={transition.before.state} variant="customer" />
                                </Box>
                                <Box width="fit-content">
                                    <OfferStatusTag state={transition.after.state} variant="customer" />
                                </Box>
                            </Box>
                        ) : null}

                        {transition.coreDataFields.map((field) => (
                            <Box key={field} display="contents">
                                <Text variant="fieldLabel">{getCoreFieldLabel(field)}</Text>
                                <Box>{formatOfferValue.coreField(field, transition.before)}</Box>
                                <Box>{formatOfferValue.coreField(field, transition.after)}</Box>
                            </Box>
                        ))}

                        {transition.commercialFields.map((field) => (
                            <Box key={field} display="contents">
                                <Text variant="fieldLabel">{getCommercialFieldLabel(field)}</Text>
                                <Box>{formatOfferValue.commercialField(field, transition.before)}</Box>
                                <Box>{formatOfferValue.commercialField(field, transition.after)}</Box>
                            </Box>
                        ))}
                    </CompareGrid>
                </Section>
            ) : null}

            {transition.productChanges.length ? (
                <Section title={translate('offer.history.section.products')} fullHeight={false}>
                    <OfferHistoryProductDiffTable productChanges={transition.productChanges} />
                </Section>
            ) : null}
        </Box>
    );
};
